'use client'

import * as React from 'react'
import { useSignUp } from '@clerk/nextjs'
import { EnvelopeSimple } from '@phosphor-icons/react'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'

export default function SignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp()
  const [isLoading, setIsLoading] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [pendingVerification, setPendingVerification] = React.useState(false)
  const [code, setCode] = React.useState('')
  const [error, setError] = React.useState('')

  const handleOAuth = async () => {
    setIsLoading(true)
    if (!isLoaded || !signUp) {
      setIsLoading(false)
      return
    }
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: '/sign-up/sso-callback',
        redirectUrlComplete: '/onboarding',
      })
    } catch {
      setIsLoading(false)
    }
  }

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setIsLoading(true)
    if (!isLoaded || !signUp) {
      setIsLoading(false)
      return
    }
    try {
      await signUp.create({
        emailAddress: email,
        password,
        legalAccepted: true,
      })
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' })
      setPendingVerification(true)
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (verificationCode: string) => {
    if (!isLoaded || !signUp || !verificationCode || verificationCode.length < 6) return
    setError('')
    setIsLoading(true)
    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      })
      if (result.status === 'complete' && setActive) {
        await setActive({ session: result.createdSessionId })
        window.location.href = '/onboarding'
      } else {
        setError('Verification incomplete. Please try again.')
        setIsLoading(false)
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Invalid verification code')
      setIsLoading(false)
    }
  }

  if (pendingVerification) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex flex-col items-center space-y-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <EnvelopeSimple className="h-6 w-6 text-primary" />
            </div>
            <h1 className="font-serif text-2xl font-normal text-foreground">
              Check your email
            </h1>
            <p className="text-sm text-muted-foreground">
              We sent a verification code to{' '}
              <span className="font-medium text-foreground">{email}</span>
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => {
                  setCode(value)
                  if (value.length === 6) {
                    handleVerify(value)
                  }
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot className="bg-card" index={0} />
                  <InputOTPSlot className="bg-card" index={1} />
                  <InputOTPSlot className="bg-card" index={2} />
                  <InputOTPSlot className="bg-card" index={3} />
                  <InputOTPSlot className="bg-card" index={4} />
                  <InputOTPSlot className="bg-card" index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && (
              <p className="text-center text-sm text-destructive">{error}</p>
            )}
            {isLoading && (
              <p className="text-center text-sm text-muted-foreground">
                Verifying...
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-background px-4 py-8">
      <div />
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center gap-6">
          <Image
            src="/favicons/haven-rounded.png"
            alt="Haven"
            width={48}
            height={48}
            className="rounded-xl"
          />

          <div className="text-center">
            <h1 className="font-serif text-2xl font-normal text-foreground">
              Create account
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Get started with Haven
            </p>
          </div>

          <Button
            variant="outline"
            onClick={handleOAuth}
            disabled={isLoading || !isLoaded}
            className="h-10 w-full gap-3 rounded-full bg-card shadow-sm"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                fill="#4285F4"
              />
              <path
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                fill="#34A853"
              />
              <path
                d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                fill="#FBBC05"
              />
              <path
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                fill="#EA4335"
              />
            </svg>
            {isLoading ? 'Redirecting...' : 'Continue with Google'}
          </Button>

          <div className="flex w-full items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form
            onSubmit={handleEmailSignUp}
            className="flex w-full flex-col gap-4"
          >
            <div>
              <Label htmlFor="email" className="mb-1.5">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="password" className="mb-1.5">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="mb-1.5">
                Confirm password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div id="clerk-captcha" />
            <Button
              type="submit"
              disabled={isLoading || !isLoaded || !email || !password || !confirmPassword}
              className="h-10 w-full rounded-full"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              href="/sign-in"
              className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <p className="mt-8 max-w-[400px] text-center text-xs leading-relaxed text-muted-foreground">
        By continuing, you agree to Haven&apos;s{' '}
        <Link
          href="/terms"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          href="/privacy"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Privacy Policy
        </Link>
        , and to receive periodic emails with updates.
      </p>
    </div>
  )
}
